import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import { useMediaDevice } from '@elem/compPage/Oasis/hooks';
import EfImg from '@elem/ui/ef-img';
import EcoSpace from 'eco-design/base/eco-space';
import EcoSpaceSmall from 'eco-design/base/eco-space-small';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination, Swiper as SwiperProps } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { IConf } from './conf';
import list from './conf';
import styles from './index.module.scss';
import type { prodcutItemType } from './useProductList';
import { useProductList } from './useProductList';

type BtnListsProps = {
    img: string;
};

type ListItemProps = prodcutItemType & {
    isOpen: boolean;
};

const DURATION = 6000; // 动画持续时间（毫秒）

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32ZM25 16C25 16.5523 24.5523 17 24 17H17V24C17 24.5523 16.5523 25 16 25C15.4477 25 15 24.5523 15 24V17H8C7.44772 17 7 16.5523 7 16C7 15.4477 7.44772 15 8 15H15V8C15 7.44772 15.4477 7 16 7C16.5523 7 17 7.44772 17 8L17 15H24C24.5523 15 25 15.4477 25 16Z"
            fill="url(#paint0_linear_2539_59259)"
        />
        <defs>
            <linearGradient id="paint0_linear_2539_59259" x1="15.5" y1="-23.5" x2="50.4337" y2="1.78811" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FAA10E" />
                <stop offset="1" stopColor="#E35D14" />
            </linearGradient>
        </defs>
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.68629 27.3137C10.9347 33.5621 21.0653 33.5621 27.3137 27.3137C33.5621 21.0653 33.5621 10.9347 27.3137 4.68627C21.0653 -1.56212 10.9347 -1.56212 4.68629 4.68627C-1.5621 10.9347 -1.5621 21.0653 4.68629 27.3137ZM22.364 22.3639C21.9734 22.7545 21.3403 22.7545 20.9497 22.3639L16 17.4142L11.0503 22.3639C10.6597 22.7545 10.0266 22.7545 9.63604 22.3639C9.24552 21.9734 9.24552 21.3402 9.63604 20.9497L14.5858 16L9.63604 11.0502C9.24552 10.6597 9.24552 10.0265 9.63604 9.63601C10.0266 9.24549 10.6597 9.24549 11.0503 9.63601L16 14.5858L20.9497 9.63602C21.3403 9.24549 21.9734 9.24549 22.364 9.63602C22.7545 10.0265 22.7545 10.6597 22.364 11.0502L17.4142 16L22.364 20.9497C22.7545 21.3402 22.7545 21.9734 22.364 22.3639Z"
            fill="url(#paint0_linear_2426_75012)"
        />
        <defs>
            <linearGradient id="paint0_linear_2426_75012" x1="43.5772" y1="-12.2843" x2="50.3976" y2="30.299" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FAA10E" />
                <stop offset="1" stopColor="#E35D14" />
            </linearGradient>
        </defs>
    </svg>
);

export default function Comp() {
    const { locale } = getLocal();
    const { t } = useLang('theProduct.SectionA1');
    const { isH5 } = useMediaDevice();
    const { productList } = useProductList(locale);

    const [activeIndex, setActiveIndex] = useState(0);
    const [swiper, setSwiper] = useState<SwiperProps | null>(null);
    const [mList, setMList] = useState<ListItemProps[]>([]);

    const animationIdRef = useRef<number | null>(null);
    const initAniTimeRef = useRef<number>(0);
    const aniFrameTimeRef = useRef<number>(0);
    // 停止动画方法
    const stopAnimation = useCallback(() => {
        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current);
            animationIdRef.current = null;
        }
        // 重置所有动画状态
        initAniTimeRef.current = 0;
        initAniTimeRef.current = 0;

        // 重置SVG进度条
        const container = document.querySelector('.story .btns');
        const element = container?.querySelector<SVGCircleElement>('.circle circle');
        if (element) {
            element.style.strokeDashoffset = '100px';
        }
    }, []);

    // 执行动画的函数
    const animate = useCallback(() => {
        if (!initAniTimeRef.current) {
            initAniTimeRef.current = new Date().getTime();
        }
        aniFrameTimeRef.current = new Date().getTime();

        const container = document.querySelector('.story .btns');
        const element = container?.querySelector('.circle circle');

        // 计算动画进度（0 到 1 之间的值）
        if (element && swiper && container) {
            const judgeTime = aniFrameTimeRef.current - initAniTimeRef.current;
            const progress = Math.min(judgeTime / DURATION, 1);
            (element as SVGCircleElement).style.strokeDashoffset = `${100 - progress * 100}px`;
            if (judgeTime >= DURATION) {
                swiper.slideNext();
            }
            animationIdRef.current = requestAnimationFrame(animate);
        }
    }, [swiper]);

    const doAnimate = useCallback(() => {
        if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = requestAnimationFrame(animate);
    }, [animate]);

    const swiperConfig = {
        modules: [Pagination],
        slidesPerView: 'auto' as const,
        initialSlide: 0,
        loop: true,
        loopAdditionalSlides: 3, // 增加额外的幻灯片数量
        loopPreventsSlide: false, // 防止循环模式下的滑动问题
        watchSlidesProgress: true,
        observer: true, // 监视DOM变化
        observeParents: true, // 监视父元素变化
        spaceBetween: 16,
        pagination: {
            clickable: true,
        },
        onSwiper: (swipwerIns: SwiperProps) => {
            setTimeout(() => {
                setSwiper(swipwerIns);
            }, 300);
        },
        onSlideChange: (swiper: SwiperProps) => {
            if (!swiper || typeof swiper.realIndex === 'undefined') return;
            setActiveIndex(swiper.realIndex);
            initAniTimeRef.current = 0;
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
                animationIdRef.current = null;
            }

            doAnimate();
        },
    };

    const handlePrev = () => {
        swiper?.slidePrev();
    };

    const handleNext = () => {
        swiper?.slideNext();
    };

    const usedList = useMemo(() => list[locale] || [], [locale]);
    const btnlists: BtnListsProps[] = useMemo(
        () =>
            usedList.map((item) => ({
                img: item?.h5,
            })),
        [usedList],
    );

    const handleExpand = (index: number) => {
        const newList = [...mList];
        const isOpen = newList[index]?.isOpen;
        if (isOpen) {
            doAnimate();
        } else {
            stopAnimation();
        }

        newList[index].isOpen = !newList[index].isOpen;
        setMList(newList);
    };

    const handleBuyNow = useCallback(
        (link: string, productName: string) => {
            if (link) {
                try {
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'checkout',
                        button_name: `${t('buy_now')}_${productName}`,
                    });
                } catch (e) {
                    console.log(e);
                }
                window.open(link, '_blank');
            }
        },
        [t],
    );

    const handleClickCard = useCallback(
        (index: number) => {
            setActiveIndex(index);
            try {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'product_detail_page',
                    list_name: t('title'),
                    button_name: `Scene_${index + 1}`,
                });
            } catch (e) {
                console.log(e);
            }
        },
        [t],
    );

    useEffect(() => {
        return () => {
            // 组件卸载时清理
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current);
                animationIdRef.current = null;
            }
            if (swiper) {
                swiper.destroy(true, true);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderProductCard = useCallback(
        (index: number) => (
            <div className="product-card">
                <div className="product-img">
                    {productList[index]?.variantImg && (
                        <EfImg pc={productList[index]?.variantImg} pad={productList[index]?.variantImg} h5={productList[index]?.variantImg} />
                    )}
                </div>
                <div className="product-info">
                    <div className="title">{productList[index]?.title}</div>
                    <div className="desc">{productList[index]?.desc}</div>
                    <div className="price-container-out">
                        <div className="price-container">
                            <div className="price1">{productList[index]?.price}</div>
                            {productList[index]?.compareAtPrice !== productList[index]?.price && (
                                <div className="price2">{productList[index]?.compareAtPrice}</div>
                            )}
                        </div>
                        <div className="btn-buy" onClick={() => handleBuyNow(productList[index]?.link_buy, productList[index]?.title)}>
                            {t('buy_now')}
                        </div>
                    </div>
                </div>
            </div>
        ),
        [productList, handleBuyNow, t],
    );

    useEffect(() => {
        if (typeof window !== 'undefined' && swiper && isH5) {
            doAnimate();
        }
    }, [swiper, doAnimate, isH5]);

    useEffect(() => {
        if (productList.length) {
            const newList = productList.map((item) => ({
                ...item,
                isOpen: false,
            }));
            setMList(newList);
        }
    }, [productList]);

    return (
        <div className={styles.SectionA1}>
            <EcoSpace />
            <EcoSectionCommon title={t('title')} />
            <EcoSpaceSmall />
            {!isH5 ? (
                <div className="slides-container">
                    <div className="main-content">
                        {usedList.map((item: IConf, index: number) => (
                            <div key={index} className={`item ${activeIndex === index ? 'active' : ''}`}>
                                <EfImg className="item-img" pc={item.pc} pad={item.pc} h5={item.h5} />
                                {renderProductCard(index)}
                            </div>
                        ))}
                    </div>
                    <div className="controller">
                        {btnlists.map((btn, index: number) => (
                            <div
                                className={`pic-btn ${activeIndex === index ? 'active' : ''}`}
                                key={index}
                                onClick={() => {
                                    handleClickCard(index);
                                }}
                            >
                                <EfImg className="item-img" pc={btn.img} h5={btn.img} />
                                {activeIndex !== index && <div className="mask"></div>}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                mList.length > 0 && (
                    <>
                        <Swiper className="story" {...swiperConfig}>
                            {mList.map((item, index) => (
                                <SwiperSlide className={`card item-${index + 1}`} key={index}>
                                    <div className="item-img">
                                        <EfImg pc={item?.pc} pad={item?.pc} h5={item?.h5} />
                                    </div>
                                    <div className="item-info">
                                        <div className="item-desc">{t(`story${index + 1}_desc`)}</div>
                                    </div>
                                    <div className="product-expand">
                                        <div
                                            className="expand-icon"
                                            onClick={() => {
                                                handleExpand(index);
                                            }}
                                        >
                                            {!item.isOpen ? <PlusIcon /> : <CloseIcon />}
                                        </div>
                                        <div className={`product-item ${item.isOpen ? 'show' : ''}`}>
                                            <EfImg className="item-img" pc={item.variantImg} h5={item.variantImg} />
                                            <div className="text-container">
                                                <div className="title">{item.title}</div>
                                                <div className="desc">{item.desc}</div>
                                            </div>
                                            <div className="product-item-bottom">
                                                <div className="price-container">
                                                    <div className="price1">{item.price}</div>
                                                    <div className="price2">{item.compareAtPrice}</div>
                                                </div>
                                                <div className="btn-buy" onClick={() => handleBuyNow(item?.link_buy, item.title)}>
                                                    {t('buy_now')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                            <div className="btns">
                                <span className={`btn prev ${swiper?.activeIndex === 0 ? 'disabled' : ''}`} onClick={handlePrev}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54" fill="none">
                                        <g clipPath="url(#clip0_35_6276)">
                                            <circle cx="27" cy="27" r="27" transform="matrix(-1 0 0 1 54 0)" fill="#F4F4F4" />
                                            <path
                                                d="M29.25 33.75L22.5 27L29.25 20.25"
                                                stroke="black"
                                                strokeWidth="2.25"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_35_6276">
                                                <rect width="54" height="54" fill="white" transform="matrix(-1 0 0 1 54 0)" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </span>
                                <span className="btn next" onClick={handleNext}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54" fill="none">
                                        <g clipPath="url(#clip0_35_6279)">
                                            <circle cx="27" cy="27" r="27" fill="#F4F4F4" />
                                            <path
                                                d="M24.75 33.75L31.5 27L24.75 20.25"
                                                stroke="black"
                                                strokeWidth="2.25"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_35_6279">
                                                <rect width="54" height="54" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <svg className="circle" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" preserveAspectRatio="none">
                                        <circle
                                            cx="18"
                                            cy="18"
                                            r="16"
                                            fill="transparent"
                                            stroke="#FAA10E"
                                            strokeWidth="2"
                                            strokeDashoffset="100"
                                            strokeLinecap="round"
                                            strokeDasharray="100 100"
                                        />
                                    </svg>
                                </span>
                            </div>
                        </Swiper>
                    </>
                )
            )}
        </div>
    );
}
